import React, { useCallback, useEffect, useRef, useState } from 'react';
import HistoryPreview from '../common/HistoryPreview';
import SharePdf from '../common/SharePdf';
import ShareQualia from '../common/ShareQualia';
import FilePopup from '../common/FilePopup';
import { REPORT_GENERATION_MODES } from '../../helpers/constants';
import { makeRESTApiRequest } from '../../helpers/api';
import { getServiceSubmitter, isQualiaOrganization } from '../../helpers/global';
import { normalizePdfHighlights } from '../../helpers/pdfHighlight';
import { findOrderOnService } from './utils/helpers';

const ReportSidebarFooter = ({
  historyReport,
  showHistory,
  onCloseHistory,
  handleHideGeneratedFile,
  fileShareMetadata,
  setFileShareMetadata,
  service,
  report,
  reportId,
  onHistoryReportChange,
  shareEndpoint,
  generatedFile,
  reportGenerationMode,
  isGeneratingReport,
  onFileShareClick,
  onRefreshFile,
  showIsPreliminaryCheckbox = false,
  saveEndpoint,
  onReportHighlightsChange,
  onShareComplete
}) => {
  const [sharePdfUrlOverride, setSharePdfUrlOverride] = useState(null);
  const [qualiaPdfUrlOverride, setQualiaPdfUrlOverride] = useState(null);
  const [showQualiaModal, setShowQualiaModal] = useState(false);
  const [pdfHighlights, setPdfHighlights] = useState(() => normalizePdfHighlights(report?.pdfHighlights));
  const persistInFlightRef = useRef(false);

  const isQualiaClient = isQualiaOrganization(service?.organization);
  const folderId = service?.driveFolder;
  const submitter = getServiceSubmitter(service);

  const lienOrder = findOrderOnService(service, report?.orderId, 'Lien Search');
  const isPreliminaryReport = lienOrder?.status === 'Preliminary Report';

  useEffect(() => {
    setPdfHighlights(normalizePdfHighlights(report?.pdfHighlights));
  }, [report?._id, report?.pdfHighlights]);

  const handleShareClick = bakedPdfUrl => {
    setSharePdfUrlOverride(bakedPdfUrl || null);
    onFileShareClick?.();
  };

  const handleQualiaShareClick = bakedPdfUrl => {
    setQualiaPdfUrlOverride(bakedPdfUrl || null);
    setShowQualiaModal(true);
  };

  const handleCloseQualiaModal = () => {
    setShowQualiaModal(false);
    setQualiaPdfUrlOverride(null);
  };

  const handleCloseShare = () => {
    setFileShareMetadata(null);
    setSharePdfUrlOverride(null);
  };

  const persistHighlights = useCallback(
    async highlights => {
      const normalized = normalizePdfHighlights(highlights);
      setPdfHighlights(normalized);
      onReportHighlightsChange?.(normalized);

      if (!reportId || !saveEndpoint || persistInFlightRef.current) {
        return;
      }

      persistInFlightRef.current = true;
      try {
        const { error, response } = await makeRESTApiRequest({
          endpoint: saveEndpoint(reportId),
          method: 'PUT',
          requestBody: {
            pdfHighlights: normalized,
            skipValidation: true
          }
        });
        if (error) {
          console.error('Failed to persist PDF highlights', error);
          return;
        }
        if (response?.pdfHighlights) {
          setPdfHighlights(normalizePdfHighlights(response.pdfHighlights));
        }
      } finally {
        persistInFlightRef.current = false;
      }
    },
    [reportId, saveEndpoint, onReportHighlightsChange]
  );

  return (
    <>
      <HistoryPreview
        reports={historyReport}
        showHistory={showHistory}
        onCloseHistory={onCloseHistory}
        dockPosition="left"
        handleHideGeneratedFileFromReport={handleHideGeneratedFile}
      />

      <ShareQualia
        show={showQualiaModal}
        onClose={handleCloseQualiaModal}
        pdfUrl={qualiaPdfUrlOverride || generatedFile?.href}
        reportId={reportId}
        folderId={folderId}
        shareEndpoint={shareEndpoint}
        onHistoryReportChange={onHistoryReportChange}
        onShareComplete={onShareComplete}
        isPreliminaryReportStatus={isPreliminaryReport}
        isGeneratedReportWatermark={generatedFile?.isWaterMark === true}
      />

      <SharePdf
        pdfUrl={sharePdfUrlOverride || generatedFile?.href}
        show={fileShareMetadata}
        onClose={handleCloseShare}
        buyerEmail={service?.buyerEmail}
        requestedByEmail={service?.requestedBy?.email || ''}
        reportId={reportId}
        report={report}
        onHistoryReportChange={onHistoryReportChange}
        onShareComplete={onShareComplete}
        isIndividualService={service?.isIndividualService}
        individualEmail={service?.individual?.email}
        folderId={folderId}
        organization={service?.organization?._id || service?.organization}
        shareEndpoint={shareEndpoint}
        showIsPreliminaryCheckbox={showIsPreliminaryCheckbox}
        lockPreliminaryReportCheckbox={isPreliminaryReport}
        isGeneratedReportWatermark={generatedFile?.isWaterMark === true}
        submittedByName={submitter.name}
        submittedByEmail={submitter.email}
        specialInstructions={service?.modificationAndSpecialInstructions || ''}
      />

      {generatedFile && (
        <FilePopup
          file={generatedFile}
          mode={reportGenerationMode}
          isGeneratingReport={isGeneratingReport}
          onHide={handleHideGeneratedFile}
          onShareClick={reportGenerationMode === REPORT_GENERATION_MODES.PREVIEW_AND_SHARE ? handleShareClick : null}
          showQualiaShareButton={isQualiaClient}
          onQualiaShareClick={
            reportGenerationMode === REPORT_GENERATION_MODES.PREVIEW_AND_SHARE ? handleQualiaShareClick : null
          }
          halfScreen
          dock="right"
          refreshFile={onRefreshFile}
          enableAnnotations
          initialHighlights={pdfHighlights}
          onHighlightsPersist={persistHighlights}
        />
      )}
    </>
  );
};

export default ReportSidebarFooter;
